import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import { router } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { useGenres } from "@hooks/trpc/useGenres";
import { useMusicSyncSaveGenres } from "@hooks/trpc/useMusicSync";
import { ActivityIndicator, FlatList } from "react-native";
import { useOnboardingStore } from "./store/onboardingStore";
import { useFlashingBorder } from "./useFlashingBorder";
import { Platform } from "react-native";
import * as Haptics from "expo-haptics";
import { fonts, theme } from "@theme";
import { captureException } from "@utils/captureException";
import { sanitizeHexColor } from "@utils/colors";
import { trackOnb } from "./analytics/trackOnboarding";
import { toast } from "sonner-native";

type Genre = {
  id: string;
  name: string;
  image: string | null;
  primaryColour: string;
  secondaryColour: string;
};

const numColumns = 3;
const gap = 12;

const MAX_GENRES = 3;

const PickGenres = () => {
  const journey = useOnboardingStore((s) => s.journey) ?? "signup";

  useEffect(() => {
    trackOnb(journey, "pick-genres", "view");
  }, [journey]);
  const {
    query: {
      data: paginatedGenres,
      isLoading,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    },
  } = useGenres(26);
  const [selectedGenres, setSelectedGenres] = useState<Genre[]>([]);

  const { mutateAsync: saveGenres } = useMusicSyncSaveGenres();

  const onSubmit = async () => {
    if (selectedGenres.length === 0) return;

    trackOnb(journey, "pick-genres", "continue-pressed", {
      genres: selectedGenres.map((g) => g.id),
      count: selectedGenres.length,
    });

    useOnboardingStore
      .getState()
      .setSelectedGenres(selectedGenres.map((g) => ({ id: g.id })));

    await saveGenres({
      genres: selectedGenres.map((g) => ({ id: g.id })),
    });

    router.replace("/onboarding/notification");
  };

  const genres = useMemo(
    () => paginatedGenres?.pages.flatMap((page) => page.data) ?? [],
    [paginatedGenres?.pages],
  );

  return (
    <LScreen safeArea pt="l1" gap="m2" pb="l1">
      <LBox gap="s2" mb="m1" mt="m2">
        <LText variant="h2" textAlign="center">
          What kind of music do you vibe with?
        </LText>
        <LText variant="bodyM2" color="whiteTranslucent50" textAlign="center">
          Select your favorite genres
        </LText>
      </LBox>
      <FlatList
        data={genres}
        ListEmptyComponent={
          isLoading ? (
            <LBox
              maxWidth={300}
              mt="h_l3"
              alignItems="center"
              alignSelf="center"
            >
              <ActivityIndicator size="large" />
            </LBox>
          ) : (
            <LBox
              maxWidth={300}
              mt="h_l3"
              alignItems="center"
              alignSelf="center"
            >
              <LText color="darkGrey" textAlign="center">
                No genres found
              </LText>
            </LBox>
          )
        }
        keyExtractor={(item) => item.id}
        renderItem={({ item: genre, index }) => (
          <GenreItem
            key={genre.id}
            enableBorderFlashing={index === 0 && selectedGenres.length === 0}
            genre={genre}
            toggleSelected={() => {
              setSelectedGenres((prev) => {
                const wasSelected = prev.some((g) => g.id === genre.id);
                if (wasSelected) {
                  trackOnb(journey, "pick-genres", "genre-pressed", {
                    genre: genre.id,
                    selected: false,
                  });
                  return prev.filter((g) => g.id !== genre.id);
                }
                if (prev.length >= MAX_GENRES) {
                  toast.success(
                    `You can only select up to ${MAX_GENRES} genres`,
                    { duration: 1500 },
                  );
                  return prev;
                }
                trackOnb(journey, "pick-genres", "genre-pressed", {
                  genre: genre.id,
                  selected: true,
                });
                return [...prev, genre];
              });
            }}
            selected={selectedGenres.some((g) => g.id === genre.id)}
          />
        )}
        numColumns={numColumns}
        columnWrapperStyle={{ gap }}
        contentContainerStyle={{ gap }}
        onEndReached={async () => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage().catch((error) => {
              console.error("Error fetching next page:", error);
            });
          }
        }}
        showsVerticalScrollIndicator={false}
      />
      <LBox pt="m1" gap="m1">
        <LBox opacity={selectedGenres.length === 0 ? 0.4 : 1}>
          <LPressable
            fadeEnabled
            sentryEventName={null}
            onPress={() => {
              if (Platform.OS !== "web") {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(
                  (error) => {
                    captureException(error, {
                      channel: "ignore",
                      area: "haptics",
                      operation: "pick-genre-impact",
                    });
                  },
                );
              }
              onSubmit().catch(console.error);
            }}
            disabled={selectedGenres.length === 0}
            style={{
              backgroundColor: theme.colors.white,
              borderRadius: 9999,
              paddingVertical: theme.spacing.s4,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LText fontSize={16} fontFamily={fonts.semiBold} color="screenBg">
              {selectedGenres.length > 0
                ? "Continue"
                : "Select at least 1 genre"}
            </LText>
          </LPressable>
        </LBox>
        <LPressable
          fadeEnabled
          sentryEventName={null}
          onPress={() => {
            trackOnb(journey, "pick-genres", "back-pressed");
            router.back();
          }}
        >
          <LText variant="bodyM2" color="whiteTranslucent50" textAlign="center">
            Go Back
          </LText>
        </LPressable>
      </LBox>
    </LScreen>
  );
};

const GenreItem = ({
  enableBorderFlashing,
  genre,
  toggleSelected,
  selected,
}: {
  enableBorderFlashing: boolean;
  genre: Genre;
  toggleSelected: () => void;
  selected: boolean;
}) => {
  const animatedBorderStyle = useFlashingBorder(enableBorderFlashing, selected);

  return (
    <LPressable
      sentryEventName={null}
      onPress={toggleSelected}
      style={{
        flex: 1 / 3,
      }}
    >
      <LBox
        borderWidth={1}
        borderBottomWidth={3}
        borderColor={selected ? "white" : "whiteTranslucent20"}
        borderRadius="m"
        overflow="hidden"
        flex={1}
        style={animatedBorderStyle}
      >
        <LBox
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          width="100%"
          height="100%"
          p="s2"
          gap="s3"
          backgroundColor="softNight"
          borderRadius="s-s"
        >
          <LBox
            backgroundColor={selected ? ["lead", "softNight"] : "softNight"}
            opacity={0.15}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius="s-s"
          />

          {selected && (
            <LBox position="absolute" top={2} right={2} zIndex={10}>
              <LIcon name="check" size={24} />
            </LBox>
          )}

          <LArtwork
            size={80}
            url={genre.image}
            borderRadius="max"
            borderWidth={1}
            style={{
              borderColor: sanitizeHexColor(genre.primaryColour),
            }}
          />
          <LBox width="100%">
            <LText
              color="white"
              variant="bodyM2"
              fontSize={11}
              numberOfLines={2}
              textAlign="center"
              adjustsFontSizeToFit
            >
              {genre.name}
            </LText>
          </LBox>
        </LBox>
      </LBox>
    </LPressable>
  );
};

export default PickGenres;
